var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { EpicSearchSortBy } from "healthex-common/epic.directory.dtos";
import { FeatureFlag } from "healthex-common/features.dtos";
import { fullNameForUSAState } from "healthex-common/usaStates";
import { action, computed, observable, runInAction } from "mobx";
import { getObservableT } from "../Infra/i18next";
import { mergeAndSortLocationsByScore, toFastenSearchResult } from "../Infra/location.search.utils";
const t = getObservableT("patients");
function toEpicFilters(f) {
    if (!f)
        return undefined;
    return {
        locations: f.locations,
        sortBy: f.sortBy === "location" ? EpicSearchSortBy.LOCATION : undefined,
        sortByOpts: f.sortByOpts,
    };
}
export class FastenSearchViewModel {
    constructor(opts) {
        this.currentQuery = null;
        this.fastenResults = [];
        this.epicResults = [];
        this.fastenNextCursor = null;
        this.epicNextCursor = null;
        this.fastenTotalHits = 0;
        this.epicTotalHits = 0;
        this.suggestedFastenEndpoints = [];
        this.isSuggestedFastenEndpointsLoading = false;
        this.isModalOpen = false;
        this.searchQuery = "";
        this.isLoadingMore = false;
        this.showFilters = false;
        this.selectedState = "ALL";
        this.sortBy = "default";
        this.zipcode = "";
        this.pendingPortalSelection = null;
        this.failedLogos = {};
        this.fastenSearchManager = opts.container.fastenManager;
        this.documentRetrievalManager = opts.container.documentRetrievalManager;
        this.epicSearchManager = opts.container.epicSearchManager;
        this.featureManager = opts.container.featureManager;
        this.environment = opts.container.environment;
        this.parentModel = opts.parentModel;
    }
    get searchResults() {
        if (this.currentQuery === null)
            return null;
        const config = this.featureManager.decodeFeatureConfiguration(FeatureFlag.RO_EPIC_DIRECT);
        const mergeOptions = (config === null || config === void 0 ? void 0 : config.fastenUsefulScoreThreshold) !== undefined || (config === null || config === void 0 ? void 0 : config.epicUsefulScoreThreshold) !== undefined
            ? {
                fastenScoreThreshold: config === null || config === void 0 ? void 0 : config.fastenUsefulScoreThreshold,
                epicScoreThreshold: config === null || config === void 0 ? void 0 : config.epicUsefulScoreThreshold,
            }
            : undefined;
        let merged = mergeAndSortLocationsByScore(this.fastenResults, this.epicResults, mergeOptions);
        if (config === null || config === void 0 ? void 0 : config.useLogoCache) {
            merged = merged.map((r) => {
                if (r.source !== "epic" || !!r.logoUrl || !r.name)
                    return r;
                const logoUrl = this.fastenSearchManager.getLogoUrlByName(r.name);
                return logoUrl ? { ...r, logoUrl } : r;
            });
        }
        const fastenExhausted = this.fastenResults.length >= this.fastenTotalHits || this.fastenNextCursor === null;
        const epicExhausted = this.epicResults.length >= this.epicTotalHits || this.epicNextCursor === null;
        const limit = fastenExhausted || epicExhausted ? merged.length : Math.min(this.fastenResults.length, this.epicResults.length);
        const result = merged.slice(0, limit);
        if ((config === null || config === void 0 ? void 0 : config.debug) && result.length > 0) {
            const debugEntries = result.map((r) => {
                var _a, _b;
                return ({
                    name: (_a = r.name) !== null && _a !== void 0 ? _a : "",
                    score: (_b = r.score) !== null && _b !== void 0 ? _b : null,
                    source: r.source === "epic" ? "E" : "F",
                });
            });
            console.log("[FastenSearchViewModel] combined search results (debug)", debugEntries);
        }
        return result;
    }
    get epicDirectDebug() {
        const config = this.featureManager.decodeFeatureConfiguration(FeatureFlag.RO_EPIC_DIRECT);
        return (config === null || config === void 0 ? void 0 : config.debug) === true;
    }
    get totalHits() {
        return this.fastenTotalHits + this.epicTotalHits;
    }
    get hasMore() {
        const fastenHasMore = this.fastenResults.length < this.fastenTotalHits && this.fastenNextCursor !== null;
        const epicHasMore = this.epicResults.length < this.epicTotalHits && this.epicNextCursor !== null;
        return fastenHasMore || epicHasMore;
    }
    get shouldShowSuggestedFastenEndpoints() {
        return this.searchResults === null && (this.isSuggestedFastenEndpointsLoading || this.suggestedFastenEndpoints.length > 0);
    }
    clearSearch() {
        this.currentQuery = null;
        this.fastenResults = [];
        this.epicResults = [];
        this.fastenNextCursor = null;
        this.epicNextCursor = null;
        this.fastenTotalHits = 0;
        this.epicTotalHits = 0;
        this.pendingPortalSelection = null;
        this.showFilters = false;
        this.failedLogos = {};
    }
    markLogoFailed(id) {
        this.failedLogos[id] = true;
    }
    hasLogoFailed(id) {
        return id ? !!this.failedLogos[id] : false;
    }
    toggleFilters() {
        this.showFilters = !this.showFilters;
    }
    setSelectedState(state) {
        this.selectedState = state;
        void this.triggerSearch();
    }
    setSortBy(sort) {
        this.sortBy = sort;
        if (sort === "location" && this.zipcode) {
            void this.triggerSearch();
        }
        else if (sort === "default") {
            void this.triggerSearch();
        }
    }
    setZipcode(zipcode) {
        this.zipcode = zipcode;
        if (this.sortBy === "location" && zipcode.length === 5) {
            void this.triggerSearch();
        }
    }
    buildFilters() {
        const filters = {};
        filters.locations = [this.selectedState];
        if (this.sortBy === "location" && this.zipcode) {
            filters.sortBy = "location";
            filters.sortByOpts = { locationZipcodes: [this.zipcode] };
        }
        return filters;
    }
    async triggerSearch() {
        var _a;
        if ((_a = this.currentQuery) === null || _a === void 0 ? void 0 : _a.trim()) {
            await this.search(this.currentQuery);
        }
    }
    async search(query) {
        this.currentQuery = query;
        if (!(query === null || query === void 0 ? void 0 : query.trim())) {
            this.currentQuery = null;
            this.fastenResults = [];
            this.epicResults = [];
            this.fastenNextCursor = null;
            this.epicNextCursor = null;
            this.fastenTotalHits = 0;
            this.epicTotalHits = 0;
            return;
        }
        const filters = this.buildFilters();
        const epicFilters = toEpicFilters(filters);
        const epicSearchEnabled = this.featureManager.isFeatureEnabled(FeatureFlag.RO_EPIC_DIRECT);
        const [fastenResponse, epicResponse] = await Promise.all([
            this.fastenSearchManager.search(query, filters),
            epicSearchEnabled
                ? this.epicSearchManager.search(query, epicFilters)
                : Promise.resolve({ results: [], nextCursor: null, totalHits: 0 }),
        ]);
        if (this.currentQuery === query) {
            runInAction(() => {
                this.fastenResults = fastenResponse.results;
                this.epicResults = epicResponse.results;
                this.fastenNextCursor = fastenResponse.nextCursor;
                this.epicNextCursor = epicResponse.nextCursor;
                this.fastenTotalHits = fastenResponse.totalHits;
                this.epicTotalHits = epicResponse.totalHits;
            });
        }
    }
    async loadMore() {
        if (this.isLoadingMore || !this.hasMore || !this.currentQuery) {
            return;
        }
        const queryAtStart = this.currentQuery;
        this.isLoadingMore = true;
        try {
            const filters = this.buildFilters();
            const epicFilters = toEpicFilters(filters);
            const epicEnabled = this.featureManager.isFeatureEnabled(FeatureFlag.RO_EPIC_DIRECT);
            const [fastenResponse, epicResponse] = await Promise.all([
                this.fastenNextCursor
                    ? this.fastenSearchManager.search(queryAtStart, filters, this.fastenNextCursor)
                    : Promise.resolve({ results: [], nextCursor: null, totalHits: this.fastenTotalHits }),
                epicEnabled && this.epicNextCursor
                    ? this.epicSearchManager.search(queryAtStart, epicFilters, this.epicNextCursor)
                    : Promise.resolve({ results: [], nextCursor: null, totalHits: this.epicTotalHits }),
            ]);
            if (this.currentQuery === queryAtStart) {
                runInAction(() => {
                    this.fastenResults = [...this.fastenResults, ...fastenResponse.results];
                    this.epicResults = [...this.epicResults, ...epicResponse.results];
                    this.fastenNextCursor = fastenResponse.nextCursor;
                    this.epicNextCursor = epicResponse.nextCursor;
                    this.fastenTotalHits = fastenResponse.totalHits;
                    this.epicTotalHits = epicResponse.totalHits;
                });
            }
        }
        finally {
            runInAction(() => {
                this.isLoadingMore = false;
            });
        }
    }
    providerSelected(provider) {
        var _a, _b, _c;
        if (provider.source === "epic") {
            if (!provider.id || !provider.name || !provider.url) {
                console.error("Epic location missing required data", { provider: provider.id, name: provider.name, url: provider.url });
                return;
            }
            void this.parentModel.epicLocationSelected({
                id: provider.id,
                name: provider.name,
                url: provider.url,
            });
            return;
        }
        const fastenProvider = toFastenSearchResult(provider);
        if (!fastenProvider) {
            return;
        }
        const brandId = (_a = fastenProvider.brandIds) === null || _a === void 0 ? void 0 : _a[0];
        const portalsWithEndpoints = (_c = (_b = fastenProvider.portals) === null || _b === void 0 ? void 0 : _b.filter((p) => p.endpoints && p.endpoints.length > 0)) !== null && _c !== void 0 ? _c : [];
        if (!brandId || portalsWithEndpoints.length === 0) {
            console.error("Fasten provider missing required data:", { brandId, provider });
            return;
        }
        if (portalsWithEndpoints.length === 1) {
            void this.parentModel.providerSelectedWithPortal(fastenProvider, portalsWithEndpoints[0]);
            return;
        }
        this.pendingPortalSelection = {
            provider: fastenProvider,
            portals: portalsWithEndpoints,
        };
    }
    portalSelected(portal) {
        if (!this.pendingPortalSelection)
            return;
        void this.parentModel.providerSelectedWithPortal(this.pendingPortalSelection.provider, portal);
        this.pendingPortalSelection = null;
    }
    cancelPortalSelection() {
        this.pendingPortalSelection = null;
    }
    handleResultClick(result) {
        this.providerSelected(result);
        if (!this.pendingPortalSelection) {
            this.isModalOpen = false;
            this.searchQuery = "";
        }
    }
    handlePortalClick(portal) {
        this.portalSelected(portal);
        this.isModalOpen = false;
        this.searchQuery = "";
    }
    handleOpenModal() {
        this.parentModel.fastenAuthError = null;
        this.parentModel.fastenCustomSearchModalOpen = true;
        this.isModalOpen = true;
        void this.loadSuggestedFastenEndpoints();
    }
    handleCloseModal() {
        this.isModalOpen = false;
        this.parentModel.fastenCustomSearchModalOpen = false;
        this.searchQuery = "";
        this.clearSearch();
        this.suggestedFastenEndpoints = [];
        this.isSuggestedFastenEndpointsLoading = false;
    }
    setSearchQuery(query) {
        this.searchQuery = query;
        void this.search(query);
    }
    getPortalDisplayUrl(portalWebsite) {
        if (!portalWebsite)
            return null;
        try {
            return new URL(portalWebsite).hostname;
        }
        catch {
            return portalWebsite;
        }
    }
    hasMultiplePortals(provider) {
        var _a, _b;
        return ((_b = (_a = provider.portals) === null || _a === void 0 ? void 0 : _a.filter((p) => p.endpoints && p.endpoints.length > 0).length) !== null && _b !== void 0 ? _b : 0) > 1;
    }
    formatLocations(locations) {
        if (!locations || locations.length === 0)
            return null;
        if (locations.includes("ALL"))
            return t("Nationwide");
        const stateNames = locations
            .map((code) => {
            const stateRecord = fullNameForUSAState;
            return stateRecord[code];
        })
            .filter((name) => !!name);
        if (stateNames.length === 0)
            return null;
        if (stateNames.length <= 2)
            return stateNames.join(", ");
        const displayedStates = stateNames.slice(0, 2).join(", ");
        const remaining = stateNames.length - 2;
        return `${displayedStates} + ${remaining} ${t("more")}`;
    }
    getResultSubtitleLines(result) {
        var _a;
        if (result.source === "epic") {
            return [(_a = result.careFacilityName) !== null && _a !== void 0 ? _a : null];
        }
        return [this.formatLocations(result.locations)];
    }
    async loadSuggestedFastenEndpoints() {
        var _a;
        const batchId = (_a = this.documentRetrievalManager.retrieval) === null || _a === void 0 ? void 0 : _a.batchId;
        if (!batchId)
            return;
        this.isSuggestedFastenEndpointsLoading = true;
        try {
            const suggestions = await this.documentRetrievalManager.getSuggestedFastenEndpoints(batchId);
            suggestions.sort((a, b) => {
                var _a, _b;
                const nameA = ((_a = a.portalName) !== null && _a !== void 0 ? _a : a.brandName).toLowerCase();
                const nameB = ((_b = b.portalName) !== null && _b !== void 0 ? _b : b.brandName).toLowerCase();
                return nameA < nameB ? -1 : nameA > nameB ? 1 : 0;
            });
            runInAction(() => {
                this.suggestedFastenEndpoints = suggestions;
            });
        }
        catch (error) {
            console.error("Failed to load suggested fasten endpoints", error);
        }
        finally {
            runInAction(() => {
                this.isSuggestedFastenEndpointsLoading = false;
            });
        }
    }
    handleSuggestedFastenEndpointClick(suggestion) {
        void this.parentModel.suggestedFastenEndpointSelected(suggestion);
        this.isModalOpen = false;
        this.searchQuery = "";
    }
    getSuggestionLogoUrl(suggestion) {
        return `${this.environment.FASTEN_LOGO_BASE_URL}/${suggestion.brandId}.png`;
    }
    getSuggestionStateDisplayName(suggestion) {
        var _a;
        if (!suggestion.brandState)
            return t("Nationwide");
        const stateRecord = fullNameForUSAState;
        return (_a = stateRecord[suggestion.brandState]) !== null && _a !== void 0 ? _a : suggestion.brandState;
    }
}
__decorate([
    observable
], FastenSearchViewModel.prototype, "currentQuery", void 0);
__decorate([
    observable
], FastenSearchViewModel.prototype, "fastenResults", void 0);
__decorate([
    observable
], FastenSearchViewModel.prototype, "epicResults", void 0);
__decorate([
    observable
], FastenSearchViewModel.prototype, "fastenNextCursor", void 0);
__decorate([
    observable
], FastenSearchViewModel.prototype, "epicNextCursor", void 0);
__decorate([
    observable
], FastenSearchViewModel.prototype, "fastenTotalHits", void 0);
__decorate([
    observable
], FastenSearchViewModel.prototype, "epicTotalHits", void 0);
__decorate([
    observable
], FastenSearchViewModel.prototype, "suggestedFastenEndpoints", void 0);
__decorate([
    observable
], FastenSearchViewModel.prototype, "isSuggestedFastenEndpointsLoading", void 0);
__decorate([
    observable
], FastenSearchViewModel.prototype, "isModalOpen", void 0);
__decorate([
    observable
], FastenSearchViewModel.prototype, "searchQuery", void 0);
__decorate([
    computed
], FastenSearchViewModel.prototype, "searchResults", null);
__decorate([
    observable
], FastenSearchViewModel.prototype, "isLoadingMore", void 0);
__decorate([
    computed
], FastenSearchViewModel.prototype, "epicDirectDebug", null);
__decorate([
    computed
], FastenSearchViewModel.prototype, "totalHits", null);
__decorate([
    computed
], FastenSearchViewModel.prototype, "hasMore", null);
__decorate([
    computed
], FastenSearchViewModel.prototype, "shouldShowSuggestedFastenEndpoints", null);
__decorate([
    observable
], FastenSearchViewModel.prototype, "showFilters", void 0);
__decorate([
    observable
], FastenSearchViewModel.prototype, "selectedState", void 0);
__decorate([
    observable
], FastenSearchViewModel.prototype, "sortBy", void 0);
__decorate([
    observable
], FastenSearchViewModel.prototype, "zipcode", void 0);
__decorate([
    observable
], FastenSearchViewModel.prototype, "pendingPortalSelection", void 0);
__decorate([
    observable
], FastenSearchViewModel.prototype, "failedLogos", void 0);
__decorate([
    action
], FastenSearchViewModel.prototype, "clearSearch", null);
__decorate([
    action
], FastenSearchViewModel.prototype, "markLogoFailed", null);
__decorate([
    action
], FastenSearchViewModel.prototype, "toggleFilters", null);
__decorate([
    action
], FastenSearchViewModel.prototype, "setSelectedState", null);
__decorate([
    action
], FastenSearchViewModel.prototype, "setSortBy", null);
__decorate([
    action
], FastenSearchViewModel.prototype, "setZipcode", null);
__decorate([
    action
], FastenSearchViewModel.prototype, "search", null);
__decorate([
    action
], FastenSearchViewModel.prototype, "loadMore", null);
__decorate([
    action
], FastenSearchViewModel.prototype, "providerSelected", null);
__decorate([
    action
], FastenSearchViewModel.prototype, "portalSelected", null);
__decorate([
    action
], FastenSearchViewModel.prototype, "cancelPortalSelection", null);
__decorate([
    action
], FastenSearchViewModel.prototype, "handleResultClick", null);
__decorate([
    action
], FastenSearchViewModel.prototype, "handlePortalClick", null);
__decorate([
    action
], FastenSearchViewModel.prototype, "handleOpenModal", null);
__decorate([
    action
], FastenSearchViewModel.prototype, "handleCloseModal", null);
__decorate([
    action
], FastenSearchViewModel.prototype, "setSearchQuery", null);
__decorate([
    action
], FastenSearchViewModel.prototype, "loadSuggestedFastenEndpoints", null);
__decorate([
    action
], FastenSearchViewModel.prototype, "handleSuggestedFastenEndpointClick", null);
